import type { EvidenceUploadMode } from 'api';
import { create } from 'zustand';

interface OfflineEvidenceSyncActivityScope {
  mode: EvidenceUploadMode;
  parentId?: string | number;
}

interface ActiveOfflineEvidenceActivity {
  id: string;
  mode: EvidenceUploadMode;
  parentId?: string;
}

interface OfflineEvidenceSyncActivityState {
  activeClears: Record<string, ActiveOfflineEvidenceActivity>;
  activeSyncs: Record<string, ActiveOfflineEvidenceActivity>;
  endClear: (id: string) => void;
  endSync: (id: string) => void;
  startClear: (scope: OfflineEvidenceSyncActivityScope) => string;
  startSync: (scope: OfflineEvidenceSyncActivityScope) => string;
}

const normalizeScope = ({ mode, parentId }: OfflineEvidenceSyncActivityScope) => ({
  mode,
  parentId: parentId === undefined ? undefined : String(parentId),
});

const doOfflineEvidenceSyncScopesOverlap = (
  activeScope: OfflineEvidenceSyncActivityScope,
  targetScope: OfflineEvidenceSyncActivityScope
) => {
  const active = normalizeScope(activeScope);
  const target = normalizeScope(targetScope);

  if (active.mode !== target.mode) {
    return false;
  }

  return active.parentId === undefined || target.parentId === undefined || active.parentId === target.parentId;
};

const getActivityId = ({ mode, parentId }: ReturnType<typeof normalizeScope>) =>
  `${mode}:${parentId ?? '*'}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

const removeActivity = <TActivity>(activities: Record<string, TActivity>, id: string) => {
  const remainingActivities = { ...activities };
  delete remainingActivities[id];
  return remainingActivities;
};

const addActivity = (
  activities: Record<string, ActiveOfflineEvidenceActivity>,
  scope: OfflineEvidenceSyncActivityScope
) => {
  const normalizedScope = normalizeScope(scope);
  const id = getActivityId(normalizedScope);

  return {
    id,
    nextActivities: {
      ...activities,
      [id]: {
        id,
        ...normalizedScope,
      },
    },
  };
};

export const useOfflineEvidenceSyncActivityStore = create<OfflineEvidenceSyncActivityState>((set) => ({
  activeClears: {},
  activeSyncs: {},
  endClear: (id) => {
    set(({ activeClears }) => ({ activeClears: removeActivity(activeClears, id) }));
  },
  endSync: (id) => {
    set(({ activeSyncs }) => {
      return { activeSyncs: removeActivity(activeSyncs, id) };
    });
  },
  startClear: (scope) => {
    let activityId = '';
    set(({ activeClears }) => {
      const { id, nextActivities } = addActivity(activeClears, scope);
      activityId = id;
      return { activeClears: nextActivities };
    });

    return activityId;
  },
  startSync: (scope) => {
    let activityId = '';
    set(({ activeSyncs }) => {
      const { id, nextActivities } = addActivity(activeSyncs, scope);
      activityId = id;
      return { activeSyncs: nextActivities };
    });

    return activityId;
  },
}));

export const useHasActiveOfflineEvidenceSync = (scope: OfflineEvidenceSyncActivityScope) => {
  const targetScope = normalizeScope(scope);
  return useOfflineEvidenceSyncActivityStore((state) =>
    Object.values(state.activeSyncs).some((activeSync) => doOfflineEvidenceSyncScopesOverlap(activeSync, targetScope))
  );
};

export const useHasActiveOfflineEvidenceClear = (scope: OfflineEvidenceSyncActivityScope) => {
  const targetScope = normalizeScope(scope);
  return useOfflineEvidenceSyncActivityStore((state) =>
    Object.values(state.activeClears).some((activeClear) =>
      doOfflineEvidenceSyncScopesOverlap(activeClear, targetScope)
    )
  );
};
